<script setup lang="ts">
import {computed, inject, ref} from "vue";
import SideBarUtils from "@/utils/common/SideBarUtils";
import {UIMenuItem} from "@/utils/UIConstants/UIMenuGroup";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {storeToRefs} from "pinia";
import Translator from "@/utils/common/Translator.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import {sideBarContext} from "@/utils/presenters/interfaces.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import SecurityUtils from "@/utils/login/SecurityUtils.ts";
import ListViewToolbarUtils from "@/utils/common/ListViewToolbarUtils.ts";

const {darkMode, showSidebarMenuIconColors} = storeToRefs(useNamaFlagsStore());
const context = inject(sideBarContext, {
  searchValue: ref(),
  panel: ref("home"),
  displayedMenuGroups: computed(() => []),
  searchMenuGroups: computed(() => []),
  currentGroup: ref(null),
  currentSubGroup: ref(null)
})

function onBreadcrumbHomeClicked() {
  if (context.searchValue.value)
    context.searchValue.value = '';
  context.currentGroup.value = null;
  context.currentSubGroup.value = null;
  context.panel.value = "home";
}

function onBreadcrumbGroupClicked() {
  context.panel.value = context.currentGroup.value?.groupData.code!
  context.currentSubGroup.value = null;
}

async function addEntity(item: UIMenuItem, e: Event | MouseEvent) {
  if (!SecurityUtils.userCanCreate(item.target?.targetEntity ?? ''))
    return;
  await ListViewToolbarUtils.onNewRecord(item.place?.query, SideBarUtils.shouldOpenInNewTab(e));
}

function openEntity(item: UIMenuItem, e: Event | MouseEvent) {
  if (context.goToEntity)
    context.goToEntity(item, SideBarUtils.shouldOpenInNewTab(e));
}
</script>
<template>
  <div class="tw-w-full tw-h-full tw-flex tw-flex-col tw-text-namaFontColor-menu-light dark:tw-text-namaFontColor-menu-dark dark:tw-bg-namaBackground-deep-dark">
    <div class="tw-p-2 tw-flex tw-flex-col">
      <q-breadcrumbs ref="breadcrumb" class="tw-pb-2">
        <q-breadcrumbs-el @click="onBreadcrumbHomeClicked" class="tw-cursor-pointer">
          <template #default>
            <div class="tw-flex tw-items-center">
              <NamaIcon :colored="showSidebarMenuIconColors" default-icon="nm-home" class="tw-size-5" :class="{'active-primary':context.panel.value!=='home'}"/>
              &nbsp;
              <span v-text="Translator.translateId('main')" class="tw-leading-none"/>
            </div>
          </template>
        </q-breadcrumbs-el>
        <q-breadcrumbs-el @click="onBreadcrumbGroupClicked" class="tw-cursor-pointer" v-if="ObjectChecker.isNotEmptyOrNull(context.currentGroup.value) && ObjectChecker.isEmptyOrNull(context.searchValue.value)">
          <template #default>
            <div class="tw-flex tw-items-center">
              <NamaIcon :colored="showSidebarMenuIconColors" :menu-item="context.currentGroup.value?.groupData" default-icon="nm-folders" class="tw-size-5"
                        :class="{'active-primary':context.panel.value!==context.currentGroup.value?.groupData?.code}"/>
              &nbsp;
              <span v-text="Translator.translateMenuGroup(context.currentGroup.value.groupData)"/>
            </div>
          </template>
        </q-breadcrumbs-el>
        <q-breadcrumbs-el v-if="ObjectChecker.isNotEmptyOrNull(context.currentSubGroup.value) && ObjectChecker.isEmptyOrNull(context.searchValue.value)">
          <template #default>
            <div class="tw-flex tw-items-center">
              <NamaIcon :colored="showSidebarMenuIconColors" :menu-item="context.currentSubGroup.value.subGroupData" default-icon="nm-folder" class="tw-size-5"/>
              &nbsp;
              <span v-text="Translator.translateMenuGroup(context.currentSubGroup.value.subGroupData)"/>
            </div>
          </template>
        </q-breadcrumbs-el>
      </q-breadcrumbs>
      <slot />
    </div>
    <q-scroll-area :dir="Translator.LTR" :dark="darkMode" class="fit">
      <q-tab-panels :dir="Translator.fetchDir()" v-model="context.panel.value" animated class="bg-transparent">
        <q-tab-panel name="home" class="no-padding">
          <q-list>
            <template :key="index" v-for="(group, index) in context.displayedMenuGroups.value">
              <q-item clickable @click="context.mainGroupClick?.(group)"
                      active-class="my-menu-link" v-ripple>
                <q-item-section>
                  <div class="tw-flex tw-items-center tw-gap-3">
                    <NamaIcon :colored="showSidebarMenuIconColors" :menu-item="group.groupData" default-icon="nm-folders"/>
                    <span v-text="Translator.translateMenuGroup(group.groupData)"/>
                  </div>
                </q-item-section>
                <q-item-section avatar v-if="group.subGroups">
                  <q-icon :name="'chevron_'+(Translator.isLanguageRTL()?'left':'right')">
                  </q-icon>
                </q-item-section>
              </q-item>
              <q-separator class="dark:tw-bg-[#6C757D]"></q-separator>
            </template>
          </q-list>
        </q-tab-panel>
        <template :key="index" v-for="(group, index) in context.displayedMenuGroups.value">
          <q-tab-panel :name="group.groupData.code" class="no-padding">
            <q-list>
              <template :key="index2" v-for="(subGroup, index2) in group.subGroups">
                <q-item clickable @click="context.onSubGroupClicked?.(subGroup)"
                        active-class="my-menu-link" v-ripple>
                  <q-item-section>
                    <div class="tw-flex tw-items-center tw-gap-3">
                      <NamaIcon :colored="showSidebarMenuIconColors" :menu-item="subGroup.subGroupData" default-icon="nm-folder"/>
                      <span class="tw-text-xs" v-text="Translator.translateMenuGroup(subGroup.subGroupData)"/>
                    </div>
                  </q-item-section>
                  <q-item-section avatar>
                    <q-icon :name="'chevron_'+(Translator.isLanguageRTL()?'left':'right')">
                    </q-icon>
                  </q-item-section>
                </q-item>
                <q-separator class="dark:tw-bg-[#6C757D]"></q-separator>
              </template>
            </q-list>
          </q-tab-panel>
        </template>
        <template :key="index" v-for="(group, index) in context.displayedMenuGroups.value">
          <template :key="index2" v-for="(subGroup, index2) in group.subGroups">
            <q-tab-panel :name="subGroup.subGroupData.code" class="no-padding">
              <q-list>
                <template :key="index3" v-for="(item, index3) in subGroup.subItems">
                  <div class="tw-w-full tw-flex tw-items-center tw-px-2 tw-py-1 tw-justify-between tw-gap-1">
                    <q-item clickable @click.stop="(event)=> openEntity(item, event)" v-ripple
                            dense
                            class="tw-grow tw-rounded-md"
                            :class="{'tw-bg-primary-light dark:tw-bg-primary-dark tw-text-namaFontColor-menu-dark': context.isCurrent && context.isCurrent(item)}"
                            :id="item.code??item.id">
                      <q-item-section>
                        <div class="tw-flex tw-items-center tw-gap-3">
                          <NamaIcon
                            :colored="showSidebarMenuIconColors"
                            :class="{'active-white':context.isCurrent && context.isCurrent(item)}"
                            :menu-item="item" default-icon="nm-monitoring"/>
                          <span v-text="SideBarUtils.translateMenuItem(item)"/>
                        </div>
                      </q-item-section>
                    </q-item>
                    <a
                      v-if="SecurityUtils.userCanCreate(item.target?.targetEntity ?? '')"
                      @click.stop="(event) => addEntity(item, event)"
                      class="hover:tw-bg-gray-200 dark:hover:tw-bg-white/20 tw-p-1.5 tw-rounded-lg tw-cursor-pointer"
                    >
                      <NamaIcon class="tw-size-[20px] active-primary" default-icon="nm-add-record"/>
                    </a>
                  </div>
                  <q-separator class="dark:tw-bg-[#6C757D]"></q-separator>
                </template>
              </q-list>
            </q-tab-panel>
          </template>
        </template>
      </q-tab-panels>
    </q-scroll-area>
  </div>
</template>
